import { useGame } from 'contexts/Game/Game';
import { useSettings } from 'contexts/Settings/Settings';
import { useTheme } from 'contexts/Theme/Theme';
import * as d3 from 'd3';
import { formatValue, getHistogramBins, getNormalDistribution, getNormalDistributionValue, getUrl, transformValue } from 'helpers';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as setup from 'setup';

const { axesMultiplier, barGap, handleDragIndicatorWidth, handleHeight, height, margin } = setup.charts;

export default function HistogramChart({
    axes,
    bars,
    bellCurve,
    controlLines,
    customCurve,
    decimals,
    multiplier,
    width,
    zoomedConversion,
    zoomedRetention,
    zoomedStep,
    onControlLineDrag,
}: HistogramChartProps) {

    const [node, setNode] = useState(d3.select(null));
    const game = useGame();
    const settings = useSettings();
    const theme = useTheme();

    const canvasRef = useCallback((ref: any) => {
        const newNode = d3.select(ref);
        setNode(newNode);
    }, []);

    const chartRect = useMemo(() => {
        return new DOMRect(
            margin.left,
            margin.top,
            Math.max(0, width - margin.left - margin.right),
            height - margin.top - margin.bottom,
        );
    }, [
        width,
    ]);

    const bins = useMemo(() => {
        return getHistogramBins(bars.map(bar => bar.value));
    }, [
        bars,
    ]);

    const scaleX = useMemo(() => {
        const maxValue = _.max(bins.map(bin => transformValue(bin.x1, multiplier, decimals))) || 0;
        const maxX = axes.x.max || Math.ceil(maxValue * axesMultiplier);
        return d3.scaleLinear()
            .domain([0, maxX])
            .range([0, chartRect.width]);
    }, [
        axes,
        bins,
        chartRect,
        decimals,
        multiplier,
    ]);

    const scaleY = useMemo(() => {
        const maxValue = _.max(bins.map(bin => bin.length)) || 0;
        const maxY = axes.y.max || Math.ceil(maxValue * axesMultiplier);
        return d3.scaleLinear()
            .domain([0, maxY])
            .range([chartRect.height, 0]);
    }, [
        axes,
        bins,
        chartRect,
    ]);

    const binsData = useMemo<HistogramChartBarData[]>(() => {
        const maxWidth = bins.length ? scaleX(transformValue(bins[0].x1 - bins[0].x0, multiplier, decimals)) : 0;
        const gap = Math.min(barGap, maxWidth / barGap);

        return bins.map(bin => {
            const bar = bars.find(bar => bar.value === bin[0]);
            const min = transformValue(bin.x0, multiplier, decimals);
            const max = transformValue(bin.x1, multiplier, decimals);

            return {
                color: bar?.color || setup.colors.primary,
                gap,
                max,
                min,
                opacity: (bar?.opacity !== undefined ? bar.opacity : 1) || (settings.debugChart ? 0.3 : 0),
                value: bin.length,
                width: maxWidth - gap,
                x0: scaleX(min),
                x1: scaleX(max),
                y: scaleY(bin.length),
            };
        });
    }, [
        bins,
        bars,
        decimals,
        multiplier,
        settings.debugChart,
        scaleX,
        scaleY,
    ]);

    const getBellCurvePath = useCallback((mean?: number, standardDeviation?: number) => {
        if (!bars.length || standardDeviation === 0) {
            return [];
        }

        // Create path function
        const getPath = d3.line()
            .curve(d3.curveCardinal.tension(0.2))
            .x(d => scaleX(d[0]) + chartRect.left)
            .y(d => scaleY(d[1]) + chartRect.top);

        // Calculate main values
        const mainValues = bars.map(bar => transformValue(bar.value, multiplier, decimals));
        const main = getNormalDistribution(mainValues);

        // Get curve values
        const curve = {
            mean: mean !== undefined ? mean : main.mean,
            standardDeviation: standardDeviation !== undefined ? standardDeviation : main.standardDeviation,
        };

        // Get points
        const count = 200;
        const maxProbability = getNormalDistributionValue(main.mean, main.mean, main.standardDeviation);
        const maxX = scaleX.domain()[1];
        const maxY = scaleY.domain()[1] / axesMultiplier;
        const points = _.range(count + 1).map(index => {
            const x = index / count * maxX;
            return [
                x,
                getNormalDistributionValue(x, curve.mean, curve.standardDeviation) / maxProbability * maxY,
            ];
        });

        // Return path
        return getPath(points as [number, number][]);
    }, [
        bars,
        chartRect,
        decimals,
        multiplier,
        scaleX,
        scaleY,
    ]);

    const drawCustomCurve = useCallback((handleType?: string, handleValue = 0) => {
        // Empty custom curve container
        const container = d3.select('.histogram-chart-custom-curve');
        container.attr('d', '');

        // Get step
        const step = (zoomedConversion !== undefined && game.steps[zoomedConversion]) || (zoomedRetention !== undefined && game.steps[zoomedRetention]) || (zoomedStep !== undefined && game.steps[zoomedStep]);
        if (!customCurve || !step) {
            return;
        }
        const stepSetup = setup.game.steps[step.index];

        // Get curve values
        let values = [step.min, step.max];
        if (zoomedConversion !== undefined) {
            values = [
                transformValue(step.conversion.min, stepSetup.conversion.multiplier, stepSetup.conversion.decimals),
                transformValue(step.conversion.max, stepSetup.conversion.multiplier, stepSetup.conversion.decimals),
            ];
        }
        if (zoomedRetention !== undefined) {
            values = [
                transformValue(step.retention.min, setup.game.retention.multiplier, setup.game.retention.decimals),
                transformValue(step.retention.max, setup.game.retention.multiplier, setup.game.retention.decimals),
            ];
        }
        if (handleType) {
            const index = handleType === 'min' ? 0 : 1;
            values[index] = handleValue;
        }
        const { mean, standardDeviation } = getNormalDistribution(values);

        // Draw curve
        const path = getBellCurvePath(mean, standardDeviation);
        container.attr('d', path)
            .attr('fill', 'none')
            .attr('stroke-width', 2)
            .attr('stroke', setup.colors.info);
    }, [
        customCurve,
        game.steps,
        zoomedConversion,
        zoomedRetention,
        zoomedStep,
        getBellCurvePath,
    ]);

    useEffect(() => {
        // Empty axes container
        const container = node.select('.histogram-chart-axes');
        container.selectChildren().remove();

        // Add X axis
        const axisX = d3.axisBottom(scaleX)
            .ticks(6)
            .tickPadding(10)
            .tickSizeInner(0)
            .tickSizeOuter(0);

        container.append('g')
            .call(axisX)
            .attr('font-family', null)
            .attr('font-size', null)
            .attr('transform', `translate(${chartRect.left}, ${chartRect.bottom})`)
            .style('font-size', '10px');

        container.append('text')
            .attr('fill', theme.darkMode ? setup.colors.white : setup.colors.black)
            .attr('text-anchor', 'start')
            .attr('x', chartRect.right + 10)
            .attr('y', chartRect.bottom + 5)
            .style('font-size', '12px')
            .text(axes.x.label);

        // Add Y axis
        const axisY = d3.axisLeft(scaleY)
            .tickPadding(10)
            .tickSizeInner(0)
            .tickSizeOuter(0);

        container.append('g')
            .call(axisY)
            .attr('font-family', null)
            .attr('font-size', null)
            .attr('transform', `translate(${chartRect.left}, ${chartRect.top})`)
            .style('font-size', '12px');

        container.append('text')
            .attr('fill', theme.darkMode ? setup.colors.white : setup.colors.black)
            .attr('text-anchor', 'middle')
            .attr('x', chartRect.left)
            .attr('y', 12)
            .style('font-size', '12px')
            .text(axes.y.label);
    }, [
        axes,
        chartRect,
        node,
        theme.darkMode,
        scaleX,
        scaleY,
    ]);

    useEffect(() => {
        // Empty bins container
        const container = node.select('.histogram-chart-bins');
        container.selectChildren().remove();

        if (!binsData.length) {
            return;
        }

        // Add elements
        container.selectAll('rect')
            .data(binsData)
            .enter()
            .append('rect')
            .attr('x', d => d.x0 + chartRect.left + d.gap)
            .attr('y', d => d.y + chartRect.top)
            .attr('width', d => d.width)
            .attr('height', d => chartRect.height - d.y)
            .style('fill', d => d.color)
            .style('opacity', d => d.opacity)
            .style('cursor', d => d.opacity ? 'pointer' : '')
            .attr('data-tooltip-id', 'chart-tooltip')
            .attr('data-tooltip-html', d => !d.opacity ? '' : `<div style="color: ${setup.colors.black}; display: flex; flex-direction: column;">
                                                        <span>
                                                            ${axes.x.tooltipLabel || axes.x.label}:
                                                            <span style="font-weight: bold;">
                                                                ${axes.x.tooltipPrefix || ''}${d.min.toLocaleString()}${axes.x.tooltipSuffix || ''} - ${axes.x.tooltipPrefix || ''}${d.max.toLocaleString()}${axes.x.tooltipSuffix || ''}
                                                            </span>
                                                        </span>
                                                        <span>
                                                            ${axes.y.label}:
                                                            <span style="font-weight: bold;">
                                                                ${axes.y.tooltipPrefix || ''}${d.value}${axes.y.tooltipSuffix || ''}
                                                            </span>
                                                        </span>
                                                    </div>`);
    }, [
        axes,
        binsData,
        chartRect,
        node,
        scaleX,
        scaleY,
    ]);

    useEffect(() => {
        // Empty bell curve container
        const container = d3.select('.histogram-chart-bell-curve');
        container.attr('d', '');

        if (!bellCurve) {
            return;
        }

        // Draw curve
        const path = getBellCurvePath();
        container.attr('d', path)
            .attr('fill', 'none')
            .attr('id', 'histogram-chart-bell-curve')
            .attr('stroke-width', 2)
            .attr('stroke', theme.darkMode ? setup.colors.white : setup.colors.black);
    }, [
        bellCurve,
        node,
        theme.darkMode,
        getBellCurvePath,
    ]);

    useEffect(() => {
        // Empty control lines container
        const container = d3.select('.histogram-chart-control-lines');
        container.selectChildren().remove();

        if (!controlLines) {
            return;
        }

        // Prepare
        const domainMax = scaleX.domain()[1];
        const draggable = !!onControlLineDrag;
        const sortedControlLines = [...controlLines].sort((a, b) => a.value < b.value ? -1 : 1);
        const offsetHandles = sortedControlLines.length === 2;

        // Parse control lines
        sortedControlLines.forEach((control, index) => {
            const { label, name, suffix } = control;
            const color = control.color || (theme.darkMode ? setup.colors.white : setup.colors.black);
            const minValue = ((offsetHandles && index && sortedControlLines[0]?.value) || 0) * multiplier;
            const maxValue = (((!offsetHandles || !index) && sortedControlLines[1]?.value * multiplier) || domainMax);
            const prefix = `${label}: ${control.prefix || ''}`;
            const value = transformValue(control.value, multiplier, decimals);
            const controlX = Math.round(Math.min(chartRect.right, scaleX(value) + chartRect.left));

            // Add element
            const element = container.append('g')
                .style('cursor', draggable ? 'grab' : 'default');

            // Add line
            element.append('line')
                .attr('y1', handleHeight + 2)
                .attr('y2', chartRect.bottom)
                .style('stroke', color)
                .style('stroke-dasharray', 6)
                .style('stroke-width', 2);

            // Add handle
            const handle = element.append('g');

            // Add handle background
            const handleBackground = handle.append('rect')
                .attr('rx', 4)
                .attr('height', handleHeight)
                .style('fill', color);

            // Add handle label
            const handleLabel = handle.append('text')
                .attr('x', (draggable && index ? handleDragIndicatorWidth : 0) + 10)
                .attr('y', 14)
                .style('fill', theme.darkMode ? setup.colors.black : setup.colors.white)
                .style('font-size', 12)
                .style('font-weight', 'bold')
                .style('text-transform', 'uppercase')
                .text(formatValue(value, decimals, prefix, suffix));

            // Add drag indicator
            const dragIndicator = !draggable ? null : handle.append('svg:image')
                .attr('xlink:href', getUrl('/assets/images/handle-drag-indicator.svg'))
                .attr('width', handleDragIndicatorWidth)
                .attr('height', handleHeight);

            // Create updater
            const update = (value: number) => {
                element.attr('transform', `translate(${scaleX(value) + chartRect.left}, 1)`);
                handleLabel.text(formatValue(value, decimals, prefix, suffix));

                const handleWidth = Math.ceil((handleLabel.node()?.getComputedTextLength() || 0) + 20) + (draggable ? handleDragIndicatorWidth : 0);
                const handleX = index ? 0 : -handleWidth;

                handle.attr('transform', `translate(${handleX}, 0)`);
                handleBackground.attr('width', handleWidth);
                dragIndicator?.attr('x', index ? -1 : (handleWidth - handleDragIndicatorWidth + 1));
            };
            update(value);

            // Add drag
            if (draggable) {
                const drag = d3.drag()
                    .on('start', e => {
                        // Get grab offset and starting value
                        e.target.grabOffset = e.x - controlX;
                        e.target.newValue = value;
                    })
                    .on('drag', e => {
                        // Get new value
                        const amount = _.clamp((e.x - margin.left - e.target.grabOffset) / chartRect.width, 0, 1);
                        const newValue = transformValue(amount, domainMax, decimals);

                        // Update handle and curve
                        if (newValue >= minValue && newValue <= maxValue) {
                            e.target.newValue = newValue;
                            update(newValue)
                            drawCustomCurve(name, newValue);
                        }
                    })
                    .on('end', e => {
                        onControlLineDrag?.(e.target.newValue / multiplier, name);
                    });
                drag(element as any);
            }
        });
    }, [
        chartRect,
        controlLines,
        decimals,
        drawCustomCurve,
        multiplier,
        onControlLineDrag,
        scaleX,
        theme.darkMode,
    ]);

    useEffect(() => {
        // Draw first custom curve
        drawCustomCurve();
    }, [
        drawCustomCurve,
    ]);

    return <svg height={height} ref={canvasRef} viewBox={`0 0 ${width} ${height}`} width="100%">
        <g className="histogram-chart-bins" />
        <g className="histogram-chart-axes" />
        <path className="histogram-chart-bell-curve" />
        <path className="histogram-chart-custom-curve" />
        <g className="histogram-chart-control-lines" />
    </svg>;

}
